import type { PortalKey } from '../types';

const env = import.meta.env as unknown as Record<string, string | boolean | undefined>;

const LOCAL_PORTS: Record<PortalKey, number> = {
  customer: 5173,
  seller: 5174,
  delivery: 5175,
  operations: 5176,
  admin: 5177
};

const DEFAULT_PORTAL_BASE_URLS: Record<PortalKey, string> = {
  customer: 'https://retail.dhruvantasystems.com',
  seller: 'https://seller.retail.dhruvantasystems.com',
  delivery: 'https://delivery.retail.dhruvantasystems.com',
  operations: 'https://operations.retail.dhruvantasystems.com',
  admin: 'https://admin.retail.dhruvantasystems.com'
};

const PORTAL_ENV_KEYS: Record<PortalKey, string> = {
  customer: 'VITE_CUSTOMER_PORTAL_URL',
  seller: 'VITE_SELLER_PORTAL_URL',
  delivery: 'VITE_DELIVERY_PORTAL_URL',
  operations: 'VITE_OPERATIONS_PORTAL_URL',
  admin: 'VITE_ADMIN_PORTAL_URL'
};

const normalizeAbsoluteUrl = (value?: string): string | undefined => {
  if (typeof value !== 'string') {
    return undefined;
  }

  const trimmed = value.trim();
  if (!/^https?:\/\//i.test(trimmed)) {
    return undefined;
  }

  return trimmed.replace(/\/+$/, '');
};

const resolvePortalBaseUrl = (portal: PortalKey): string => {
  const configured = normalizeAbsoluteUrl(env[PORTAL_ENV_KEYS[portal]] as string | undefined);
  if (configured) {
    return configured;
  }

  if (portal === 'customer') {
    const legacyStoreUrl = normalizeAbsoluteUrl(env.VITE_STORE_PORTAL_URL as string | undefined);
    if (legacyStoreUrl) {
      return legacyStoreUrl;
    }
  }

  if (env.DEV) {
    return `http://localhost:${LOCAL_PORTS[portal]}`;
  }

  return DEFAULT_PORTAL_BASE_URLS[portal];
};

export const portalBaseUrls: Record<PortalKey, string> = {
  customer: resolvePortalBaseUrl('customer'),
  seller: resolvePortalBaseUrl('seller'),
  delivery: resolvePortalBaseUrl('delivery'),
  operations: resolvePortalBaseUrl('operations'),
  admin: resolvePortalBaseUrl('admin')
};

export const getPortalUrl = (portal: PortalKey, path: string = '/'): string => {
  const normalizedPath = path.startsWith('/') ? path : `/${path}`;
  return new URL(normalizedPath, `${portalBaseUrls[portal]}/`).toString();
};

export const getCustomerPortalUrl = (path: string = '/'): string => getPortalUrl('customer', path);
